import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, File, Loader2, AlertCircle } from 'lucide-react';
import { uploadFile } from '../utils/api';
import { useT } from '../i18n';

const FileUpload = ({ onSuccess }) => {
  const t = useT();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const onDrop = async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;
    
    const file = acceptedFiles[0];
    setError(null);
    setUploading(true);

    try {
      const result = await uploadFile(file);
      onSuccess(result);
    } catch (err) {
      setError(err.response?.data?.detail || t('upload.failed'));
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    multiple: false
  });

  return (
    <div>
      <div
        {...getRootProps()}
        className={`dropzone ${isDragActive ? 'dropzone-active' : ''} ${error ? 'dropzone-error' : ''}`}
        data-testid="file-upload"
      >
        <input {...getInputProps()} />
        {uploading ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-12 w-12 text-primary animate-spin" />
            <p className="text-sm text-muted-foreground">{t('upload.uploading')}</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-4">
            {isDragActive ? (
              <File className="h-16 w-16 text-primary" />
            ) : (
              <Upload className="h-16 w-16 text-muted-foreground" />
            )}
            <div>
              <p className="text-base font-medium text-foreground mb-1">
                {isDragActive ? t('upload.drop') : t('upload.cta')}
              </p>
              <p className="text-sm text-muted-foreground">{t('upload.browse')}</p>
            </div>
            <p className="text-xs text-muted-foreground">{t('upload.formats')}</p>
          </div>
        )}
      </div>
      {error && (
        <div className="mt-4 p-4 rounded-[var(--radius)] bg-red-50 border border-red-200 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">{t('upload.failedTitle')}</p>
            <p className="text-sm text-red-700 mt-1">{error}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;